import { motion } from "framer-motion";
import { useState } from "react";
import {
  Bell,
  CheckCheck,
  ShieldCheck,
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  Gift,
  TrendingUp,
} from "lucide-react";
import ScreenHeader from "../components/ScreenHeader";

const initialNotifications = [
  {
    id: "n1",
    title: "Payment sent",
    body: "€50.00 to John Martin was executed by Finshark.",
    time: "2 min ago",
    icon: ArrowUpRight,
    tone: "emerald",
    unread: true,
    tag: "Transaction",
  },
  {
    id: "n2",
    title: "New login detected",
    body: "Your account was accessed from Stockholm, Sweden on iOS.",
    time: "1 h ago",
    icon: ShieldCheck,
    tone: "slate",
    unread: true,
    tag: "Security",
  },
  {
    id: "n3",
    title: "Anna sent you money",
    body: "€120.00 received — executed by Finshark.",
    time: "Yesterday",
    icon: ArrowDownLeft,
    tone: "emerald",
    unread: false,
    tag: "Transaction",
  },
  {
    id: "n4",
    title: "Payment failed",
    body: "We couldn't complete your €35.00 transfer to Marco Rossi. No funds were moved.",
    time: "2 days ago",
    icon: AlertTriangle,
    tone: "red",
    unread: false,
    tag: "Transaction",
  },
  {
    id: "n5",
    title: "Partner update",
    body: "Finshark now supports instant transfers in 14 new corridors.",
    time: "3 days ago",
    icon: TrendingUp,
    tone: "slate",
    unread: false,
    tag: "Product",
  },
  {
    id: "n6",
    title: "Refer a friend",
    body: "Invite friends to Shwiip and earn up to €20 in fee credits.",
    time: "1 week ago",
    icon: Gift,
    tone: "slate",
    unread: false,
    tag: "Promo",
  },
];

const toneStyles = {
  emerald: "bg-emerald-50 text-emerald-600",
  slate: "bg-slate-100 text-slate-700",
  red: "bg-red-50 text-red-500",
};

export const NotificationsScreen = () => {
  const [items, setItems] = useState(initialNotifications);

  const markAllRead = () => {
    setItems((list) => list.map((n) => ({ ...n, unread: false })));
  };

  return (
    <motion.div
      initial={{ x: 20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -20, opacity: 0 }}
      transition={{ type: "spring", bounce: 0, duration: 0.3 }}
      className="h-full flex flex-col"
      data-testid="notifications-screen"
    >
      <ScreenHeader title="Notifications" testId="notifications-back-btn" />

      <div className="px-6 flex items-center justify-between pb-3">
        <div>
          <h1 className="font-display text-[26px] font-bold tracking-tight">
            Notifications
          </h1>
          <div className="text-[12px] text-gray-500 mt-0.5">
            {items.filter((n) => n.unread).length} unread
          </div>
        </div>
        <button
          data-testid="mark-all-read-btn"
          onClick={markAllRead}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 h-9 px-3 rounded-full transition-colors"
        >
          <CheckCheck className="w-3.5 h-3.5" strokeWidth={2.2} />
          Mark read
        </button>
      </div>

      <div className="flex-1 overflow-y-auto phone-scroll px-6 pb-6">
        <div className="space-y-2">
          {items.map((n) => {
            const Icon = n.icon;
            return (
              <div
                key={n.id}
                data-testid={`notification-${n.id}`}
                className={
                  "flex gap-3 p-3 rounded-2xl border transition-colors " +
                  (n.unread
                    ? "bg-white border-gray-200 shadow-sm"
                    : "bg-gray-50 border-transparent")
                }
              >
                <div
                  className={
                    "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 " +
                    toneStyles[n.tone]
                  }
                >
                  <Icon className="w-4.5 h-4.5" strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-[13px] text-gray-900 leading-tight">
                      {n.title}
                    </div>
                    {n.unread && (
                      <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0 mt-1" />
                    )}
                  </div>
                  <div className="text-[12px] text-gray-600 mt-1 leading-snug">
                    {n.body}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400">
                      {n.tag}
                    </span>
                    <span className="text-[10px] text-gray-400">•</span>
                    <span className="text-[10px] text-gray-400">{n.time}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-center gap-1.5 text-[11px] text-gray-400 mt-6">
          <Bell className="w-3 h-3" strokeWidth={2.5} />
          You're all caught up.
        </div>
      </div>
    </motion.div>
  );
};

export default NotificationsScreen;
